import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, SectionHeader } from "./Reveal";

const STATS = [
  ["6+", "Years Teaching"],
  ["3", "Languages"],
  ["2", "Courses"],
];

const EXPERTISE = [
  "Producer",
  "Multi-instrumentalist",
  "Songwriter",
  "Artists & Repertoire",
];

const PRESS = [
  {
    outlet: "Rolling Stone India",
    quote: "featured in new-music roundup",
    href: "https://rollingstoneindia.com/new-music-mushroom-lake-akash-ahuja-trapperx-naalayak-tanzeel-khan-pariah-kite/amp/",
    rot: -1.4,
  },
  {
    outlet: "Indulge Express",
    quote: "The Traffic Jam Festival · Bengaluru",
    href: "https://www.indulgexpress.com/culture/music/2026/Jun/19/bengaluru-gets-a-travelling-music-bus-meet-the-traffic-jam",
    rot: 1.2,
  },
  {
    outlet: "Nupop Records",
    quote: "co-founder · producer · A&R",
    href: null,
    rot: -0.6,
  },
  {
    outlet: "Indian Music Experience",
    quote: "teaching music",
    href: "https://indianmusicexperience.org/",
    rot: 0.9,
  },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative py-32 md:py-48">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-7">
            <SectionHeader number="01" kicker="The Teacher">
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#1c1610]">
                <FadeUp>Meet <em className="italic text-[#DB0909]">Sanjith.</em></FadeUp>
              </h2>
            </SectionHeader>

            <FadeUp delay={0.15} className="mt-12 max-w-xl">
              <p className="text-[#4a3e30] text-base leading-loose font-light first-letter:font-display first-letter:text-6xl first-letter:font-black first-letter:text-[#1c1610] first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8]">
                A guitarist, performer, and educator with over six years of experience across India. Sanjith founded ABC Guitar Lessons in 2020 to bring real musicianship directly into the hands of his students.
              </p>
            </FadeUp>

            <FadeUp delay={0.25} className="mt-14 border-l-2 border-[#950606] pl-8 max-w-lg">
              <blockquote className="font-display italic text-2xl md:text-3xl leading-snug text-[#1c1610]">
                "No matter how rough the world can be, music helps you see the beauty in it."
              </blockquote>
              <p className="mt-4 font-mono text-[11px] tracking-[0.3em] uppercase text-[#4a3e30]">— Sanjith, Founder</p>
            </FadeUp>

            <FadeUp delay={0.3} className="mt-12 max-w-xl">
              <p className="text-[#4a3e30] text-base leading-loose font-light">
                His teaching philosophy is simple: music is a language, and everyone deserves to express themselves. Whether you're picking up a guitar for the first time or unlocking deeper theory, these courses are built around you.
              </p>
            </FadeUp>

            <div className="mt-20 grid grid-cols-3 gap-6 max-w-xl" data-testid="about-stats">
              {STATS.map(([n, label], i) => (
                <FadeUp key={label} delay={0.1 * i}>
                  <p className="font-display font-black text-5xl md:text-7xl text-[#DB0909] leading-none">{n}</p>
                  <p className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30]">{label}</p>
                </FadeUp>
              ))}
            </div>

            {/* Expertise chips — quick credential row */}
            <FadeUp delay={0.15} className="mt-14 max-w-xl">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30] mb-3">Known for</p>
              <div className="flex flex-wrap gap-2" data-testid="about-expertise-chips">
                {EXPERTISE.map((e) => (
                  <span
                    key={e}
                    className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border rounded-sm bg-[#fefefc] text-[#1c1610] border-[rgba(28,22,16,0.18)]"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>

          <div className="md:col-span-5 relative mt-16 md:mt-0">
            <motion.div style={{ y: imgY }} className="relative">
              <FadeUp>
                <div className="relative overflow-hidden">
                  <img
                    src="/photos/meet-sanjith.webp"
                    alt="Sanjith with acoustic guitar in the studio"
                    className="w-full aspect-[3/4] object-cover"
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(10,10,10,0.55) 0%, transparent 42%)" }} />
                  <p className="absolute bottom-5 left-5 font-mono text-[10px] tracking-[0.3em] uppercase text-white/85">
                    Studio · The teacher's chair
                  </p>
                </div>
              </FadeUp>
            </motion.div>

            {/* Press cutouts — paper-cutout / collage citation cards */}
            <FadeUp delay={0.2} className="mt-16 md:mt-20">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30] mb-5">As featured in</p>
              <div className="flex flex-wrap gap-4" data-testid="about-press">
                {PRESS.map((p) => {
                  const Comp = p.href ? "a" : "div";
                  return (
                    <Comp
                      key={p.outlet}
                      {...(p.href && { href: p.href, target: "_blank", rel: "noopener noreferrer" })}
                      className="press-cutout group block bg-[#fefefc] border border-[rgba(28,22,16,0.16)] shadow-[3px_4px_0_rgba(28,22,16,0.09)] hover:shadow-[4px_6px_0_rgba(219,9,9,0.18)] transition-all duration-300 px-5 py-4 relative"
                      style={{ transform: `rotate(${p.rot}deg)`, minWidth: "200px" }}
                      data-testid={`about-press-${p.outlet.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                      <span aria-hidden className="absolute -top-2 left-4 w-10 h-3 bg-[#DB0909]/40 rotate-[-4deg] blur-[0.3px]" />
                      <p className="font-display font-black text-base text-[#1c1610] leading-tight">
                        {p.outlet}
                      </p>
                      <p className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-[#4a3e30]">
                        {p.quote}
                      </p>
                      {p.href && (
                        <p className="mt-2 font-mono text-[9px] tracking-[0.2em] uppercase text-[#950606] opacity-70 group-hover:opacity-100 transition-opacity">
                          Read →
                        </p>
                      )}
                    </Comp>
                  );
                })}
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
